<script>
import Logo from './Logo.vue';
import PrimaryNavItem from './PrimaryNavItem.vue';
import OptionsMenu from './OptionsMenu.vue';
import AgentDetails from './AgentDetails.vue';
import NotificationBell from './NotificationBell.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { deleteIndexedDBOnLogout } from 'dashboard/store/utils/api';

export default {
  components: {
    Logo,
    PrimaryNavItem,
    OptionsMenu,
    AgentDetails,
    NotificationBell,
    ConfirmationModal,
  },
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      helpDocsURL: wootConstants.DOCS_URL,
      showOptionsMenu: false,
    };
  },
  methods: {
    frontendURL,
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
    async clearCacheAndLogout() {
      const ok = await this.$refs.confirmationModal.showConfirmation();
      if (!ok) return;

      try {
        // 1. Clear LocalStorage and SessionStorage
        window.localStorage.clear();
        window.sessionStorage.clear();

        // 2. Clear Cookies (accessible via JS)
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i += 1) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie.trim();
          
          // Limpia cookies en diferentes paths y dominios
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/app`;
        }

        // 3. Delete IndexedDB
        try {
          await deleteIndexedDBOnLogout();
        } catch (error) {
          // Fallback manual si la función no funciona
          if (window.indexedDB && window.indexedDB.databases) {
            const dbs = await window.indexedDB.databases();
            for (const db of dbs) {
              window.indexedDB.deleteDatabase(db.name);
            }
          }
        }

        // 4. Clear Cache Storage
        if ('caches' in window) {
          const cacheNames = await window.caches.keys();
          await Promise.all(
            cacheNames.map(name => window.caches.delete(name))
          );
        }

        // 5. Unregister Service Workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(reg => reg.unregister())
          );
        }

        // 6. Llamar al logout del servidor para limpiar cookies HttpOnly
        try {
          await fetch('/auth/sign_out', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': 
                document.querySelector('meta[name="csrf-token"]')?.content || ''
            },
            credentials: 'include'
          });
        } catch (error) {
          // Ignorar errores de red, continuamos con la limpieza
          console.warn('Error al cerrar sesión en el servidor:', error);
        }

        // Mostrar mensaje de éxito antes de redirigir
        useAlert(this.$t('SIDEBAR.CACHE_CLEARED_SUCCESS'));
      } catch (error) {
        console.error('Error durante la limpieza del caché:', error);
        useAlert(this.$t('SIDEBAR.CACHE_CLEARED_ERROR'));
      } finally {
        // 7. Hard Reload con parámetro único para evitar cualquier caché
        // Usamos replace para que no quede en el historial
        // Esperamos un poco para que se vea el toast
        setTimeout(() => {
          const timestamp = Date.now();
          window.location.href = `/app/login?cache_cleared=${timestamp}`;
        }, 1500);
      }
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col justify-between w-16 h-full bg-white border-r dark:bg-slate-900 border-slate-50 dark:border-slate-800/50 rtl:border-l rtl:border-r-0"
  >
    <div class="flex flex-col items-center">
      <Logo
        :source="logoSource"
        :name="installationName"
        :account-id="accountId"
        class="m-4 mb-10"
      />
      <PrimaryNavItem
        v-for="menuItem in menuItems"
        :key="menuItem.toState"
        :icon="menuItem.icon"
        :name="menuItem.label"
        :to="menuItem.toState"
        :is-child-menu-active="menuItem.key === activeMenuItem"
      />
    </div>
    <div class="flex flex-col items-center justify-end pb-6">
      <!-- Documentación comentada - descomenta si necesitas -->
      <!-- <PrimaryNavItem
        v-if="!isACustomBrandedInstance"
        icon="book-open-globe"
        name="DOCS"
        open-in-new-page
        :to="helpDocsURL"
      /> -->
      
      <!-- Botón de limpiar caché mejorado -->
      <button
        v-tooltip.top-end="$t('SIDEBAR.CLEAR_CACHE')"
        class="flex items-center justify-center w-10 h-10 mb-2 text-slate-500 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 dark:text-slate-400 transition-colors duration-200"
        :title="$t('SIDEBAR.CLEAR_CACHE') || 'Limpiar Caché y Cerrar Sesión'"
        @click="clearCacheAndLogout"
      >
        <fluent-icon
          icon="arrow-clockwise"
          size="28"
          class="cache-icon"
        />
      </button>
      
      <NotificationBell @openNotificationPanel="openNotificationPanel" />
      <AgentDetails @toggleMenu="toggleOptions" />
      <OptionsMenu
        :show="showOptionsMenu"
        @toggleAccounts="toggleAccountModal"
        @showSupportChatWindow="toggleSupportChatWindow"
        @openKeyShortcutModal="$emit('openKeyShortcutModal')"
        @close="toggleOptions"
      />
      <ConfirmationModal
        ref="confirmationModal"
        :title="$t('SIDEBAR.CLEAR_CACHE_MODAL.TITLE')"
        :description="$t('SIDEBAR.CLEAR_CACHE_MODAL.DESCRIPTION')"
        :confirm-label="$t('SIDEBAR.CLEAR_CACHE_MODAL.CONFIRM')"
        :cancel-label="$t('SIDEBAR.CLEAR_CACHE_MODAL.CANCEL')"
      />
    </div>
  </div>
</template>

<style scoped>
/* Animación opcional para el botón de limpiar caché */
button:active {
  transform: scale(0.95);
}

button .cache-icon {
  transition: transform 0.3s ease;
}

button:hover .cache-icon {
  transform: rotate(90deg);
}
</style>